import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  DragIndicator,
  Visibility
} from '@mui/icons-material';
import api from '../services/api';

function SequenceManagePage() {
  const { clubId, tvId } = useParams();
  const navigate = useNavigate();
  const [tv, setTv] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount and when route params change only
  }, [clubId, tvId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load TV info
      const tvResponse = await api.get(`/clubs/${clubId}/tvs/${tvId}`);
      setTv(tvResponse.data);
      
      // Load sequences for this TV
      const seqResponse = await api.get(`/clubs/${clubId}/tvs/${tvId}/sequences`);
      setSequences(seqResponse.data.sort((a, b) => a.order_index - b.order_index));
      
      // Load templates
      const templatesResponse = await api.get(`/templates`);
      setTemplates(templatesResponse.data);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sequence = null) => {
    setEditingSequence(sequence || {
      template_id: '',
      duration_minutes: 1,
      content_mapping: {}
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSequence(null);
  };

  const handleSaveSequence = async () => {
    try {
      const data = {
        template_id: editingSequence.template_id,
        duration_minutes: parseInt(editingSequence.duration_minutes),
        order_index: editingSequence.id ? editingSequence.order_index : sequences.length,
        content_mapping: editingSequence.content_mapping
      };

      if (editingSequence.id) {
        // Update
        await api.put(
          `/clubs/${clubId}/tvs/${tvId}/sequences/${editingSequence.id}`,
          data
        );
      } else {
        // Create
        await api.post(
          `/clubs/${clubId}/tvs/${tvId}/sequences`,
          data
        );
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save sequence');
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    if (!window.confirm('Delete this sequence item?')) return;
    
    try {
      await api.delete(`/clubs/${clubId}/tvs/${tvId}/sequences/${sequenceId}`);
      
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete sequence');
    }
  };

  const getTotalDuration = () => {
    return sequences.reduce((sum, seq) => sum + seq.duration_minutes, 0);
  };

  const getContentTypeLabel = (contentMapping) => {
    if (!contentMapping || !contentMapping.zone1) return 'Not configured';
    const zone1 = contentMapping.zone1;
    
    const typeLabels = {
      'flight-map-club': '✈️ Club Aircraft Map',
      'flight-map-area': '✈️ Area Traffic Map',
      'sponsor-image': '🏢 Sponsor Image',
      'spotter-image': '📸 Spotter Photo',
      'aircraft-image': '✈️ Aircraft Photo',
      'weather-widget': '🌤️ Weather Widget',
      'notams': '⚠️ NOTAMs',
      'calendar': '📅 Calendar Events'
    };
    
    return typeLabels[zone1.type] || zone1.type;
  };

  if (loading) {
    return (
      <Container><Box sx={{ mt: 4 }}><Typography>Loading...</Typography></Box></Container>
    );
  }

  if (!tv) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">TV not found</Alert>
          <Button onClick={() => navigate(`/clubs/${clubId}`)} sx={{ mt: 2 }}>
            Back to Club
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/clubs/${clubId}`)}
          sx={{ mb: 2 }}
        >
          Back to Club
        </Button>
        
        <Typography variant="h4" gutterBottom>
          {tv.name} - Display Sequence
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {tv.location}
        </Typography>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${sequences.length} Items`} 
            color="primary" 
          />
          <Chip 
            label={`${getTotalDuration()} Minutes Total`} 
            color="secondary" 
          />
          <Chip 
            icon={<Visibility />}
            label="View Display"
            onClick={() => window.open(`${window.location.origin}/display/${tv.url_token}`, '_blank')}
            clickable
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Sequence List */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Sequence Items</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Add Item
              </Button>
            </Box>
            
            {sequences.length === 0 ? (
              <Alert severity="info">
                No sequence items yet. Add items to create your display rotation.
              </Alert>
            ) : (
              <List>
                {sequences.map((seq, index) => (
                  <ListItem
                    key={seq.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'background.paper'
                    }}
                  >
                    <DragIndicator sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" component="span" fontWeight="bold">
                            #{index + 1}
                          </Typography>
                          <Typography variant="body1" component="span">
                            {getContentTypeLabel(seq.content_mapping)}
                          </Typography>
                          <Chip 
                            label={`${seq.duration_minutes} min`} 
                            size="small" 
                            color="primary"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption">
                          Template: {templates.find(t => t.id === seq.template_id)?.name || 'Unknown'}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenDialog(seq)}
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteSequence(seq.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        
        {/* Info Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Display Info
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Display URL:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    bgcolor: 'grey.100', 
                    p: 1, 
                    borderRadius: 1,
                    wordBreak: 'break-all',
                    fontSize: '0.75rem'
                  }}
                >
                  {window.location.origin}/display/{tv.url_token}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Rotation Info:
                </Typography>
                <Typography variant="body2">
                  • {sequences.length} items in sequence
                </Typography>
                <Typography variant="body2">
                  • {getTotalDuration()} minutes per loop
                </Typography>
                <Typography variant="body2">
                  • Continuous rotation
                </Typography>
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => window.open(`${window.location.origin}/display/${tv.url_token}`, '_blank')}
              >
                Preview Display
              </Button>
            </CardContent>
          </Card>
          
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content Types
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                Available content types:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip label="✈️ Flight Maps" size="small" variant="outlined" />
                <Chip label="🏢 Sponsor Images" size="small" variant="outlined" />
                <Chip label="📸 Spotter Photos" size="small" variant="outlined" />
                <Chip label="🌤️ Weather" size="small" variant="outlined" />
                <Chip label="📅 Events" size="small" variant="outlined" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSequence?.id ? 'Edit Sequence Item' : 'Add Sequence Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={editingSequence?.template_id || ''}
                onChange={(e) => setEditingSequence({...editingSequence, template_id: e.target.value})}
                label="Template"
              >
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.layout_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Duration (minutes)"
              type="number"
              value={editingSequence?.duration_minutes || 1}
              onChange={(e) => setEditingSequence({...editingSequence, duration_minutes: e.target.value})}
              inputProps={{ min: 1, max: 60 }}
              fullWidth
            />
            
            <Alert severity="info">
              Content configuration will be simplified in the next update. 
              For now, sequence items will use default content based on your club's images and aircraft.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveSequence} 
            variant="contained"
            disabled={!editingSequence?.template_id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SequenceManagePage;








